import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clientAPI, involvedAPI } from '../../../services/apiServices';
import { useFeedback } from '../../../contexts/FeedbackContext';
import { Alert, Input, Modal, Select } from '../../../components/common/UI';
import { EditIcon, PlusIcon, TrashIcon } from '../../../components/common/Icons';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import QuickContactModal from './QuickContactModal';

const EMPTY = { id: null, clientId: '', involvedName: '', role: '' };
const getInvolvedName = (item) => item?.client?.name || item?.involvedName || '';
const recordTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};
const sortByCreatedDesc = (items = []) => (
  [...items].sort((a, b) => recordTime(b.createdAt || b.updatedAt || getInvolvedName(b)) - recordTime(a.createdAt || a.updatedAt || getInvolvedName(a)))
);

const InvolvedTab = ({ businessId, data = [], onRefresh }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quickContactOpen, setQuickContactOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const { confirm, notify } = useFeedback();

  const loadClients = async () => {
    clientAPI.getAll().then(({ data: response }) => setClients(response.data || []));
  };

  useEffect(() => {
    if (!modal) return;
    loadClients();
  }, [modal]);

  const rows = useMemo(() => sortByCreatedDesc(data), [data]);
  const clientOptions = useMemo(() => [
    { value: '', label: t('involved.selectContact') },
    ...clients.map((client) => ({ value: client.id, label: client.name })),
  ], [clients, t]);

  const openCreate = () => {
    setForm(EMPTY);
    setError('');
    setModal(true);
  };

  const openEdit = (item) => {
    setForm({ id: item.id, clientId: item.clientId || item.client?.id || '', involvedName: item.involvedName || '', role: item.role || '' });
    setError('');
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.clientId && !form.involvedName.trim()) {
      setError(t('involved.selectContactOrNameError'));
      return;
    }

    setSaving(true);
    setError('');
    const payload = {
      clientId: form.clientId || null,
      involvedName: form.clientId ? null : (form.involvedName.trim() || null),
      role: form.role,
    };

    try {
      if (form.id) await involvedAPI.update(businessId, form.id, payload);
      else await involvedAPI.create(businessId, payload);
      setModal(false);
      setForm(EMPTY);
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err, t('involved.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickContactCreated = async (contact) => {
    await loadClients();
    setForm((current) => ({ ...current, clientId: contact.id, involvedName: '' }));
  };

  const deleteDescription = (item) => {
    const name = getInvolvedName(item) || t('involved.removedContact');
    const role = item.role || t('involved.defaultRole');
    return t('involved.removeDetailMessage', { detail: `${name} - ${role}` });
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      variant: 'danger',
      title: t('involved.removeTitle'),
      message: deleteDescription(item),
      confirmText: t('involved.remove'),
    });
    if (!ok) return;
    try {
      await involvedAPI.remove(businessId, item.id);
      onRefresh();
    } catch {
      notify({ variant: 'danger', title: t('involved.removeErrorTitle'), message: t('involved.removeError') });
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-soft">
      <div className="flex min-h-16 items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
        <h2 className="font-bold text-slate-900">{t('involved.title')}</h2>
        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-sky-500 px-5 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600" onClick={openCreate} type="button">
          <PlusIcon className="h-4 w-4" />
          {t('involved.add')}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">{t('involved.empty')}</p>
      ) : (
        <div className="divide-y divide-slate-200">
          {rows.map((item) => (
            <div key={item.id} className="flex min-h-[76px] cursor-pointer items-center gap-4 px-4 py-3 transition hover:bg-slate-50" onClick={() => setDetail(item)} role="button" tabIndex={0}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-50 font-bold text-sky-500">
                {getInvolvedName(item)?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-slate-900">{getInvolvedName(item) || t('involved.removedContact')}</p>
                <p className="mt-1 text-sm text-slate-600">{item.role || t('involved.defaultRole')}</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">{item.role || t('involved.defaultRole')}</span>
              <div className="flex gap-1">
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100" onClick={(event) => { event.stopPropagation(); openEdit(item); }} type="button" aria-label={t('involved.edit')}>
                  <EditIcon className="h-4 w-4" />
                </button>
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" onClick={(event) => { event.stopPropagation(); handleDelete(item); }} type="button" aria-label={t('involved.delete')}>
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal} onClose={() => setModal(false)} title={form.id ? t('involved.edit') : t('involved.add')} size="sm">
        {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}
        <div className="mt-4 space-y-4">
          <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
            <Select
              label={t('involved.contactLabel')}
              value={form.clientId}
              onChange={(event) => setForm({
                ...form,
                clientId: event.target.value,
                involvedName: event.target.value ? '' : form.involvedName,
              })}
              options={clientOptions}
            />
            <button className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 text-sm font-bold text-sky-700 hover:bg-sky-100" onClick={() => setQuickContactOpen(true)} type="button">
              <PlusIcon className="h-4 w-4" />
              {t('quickContact.shortAction')}
            </button>
          </div>
          <Input
            label={t('involved.manualNameLabel')}
            value={form.involvedName}
            onChange={(event) => setForm({ ...form, involvedName: event.target.value })}
            placeholder={t('involved.manualNamePlaceholder')}
            helpText={form.clientId ? t('involved.manualNameDisabledHelp') : t('involved.manualNameHelp')}
            disabled={Boolean(form.clientId)}
          />
          <Input label={t('involved.roleLabel')} value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} placeholder={t('involved.rolePlaceholder')} />
          <div className="flex gap-2">
            <button className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-500 px-5 text-sm font-bold text-white disabled:opacity-60" onClick={handleSave} disabled={saving} type="button">
              {saving ? t('common.saving') : t('common.save')}
            </button>
            <button className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 text-sm font-bold text-slate-900" onClick={() => setModal(false)} type="button">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={getInvolvedName(detail) || t('involved.title')} size="sm">
        {detail && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Info label={t('common.name')} value={getInvolvedName(detail) || t('involved.removedContact')} />
            <Info label={t('involved.roleLabel')} value={detail.role || t('involved.defaultRole')} />
            <Info label={t('auth.email')} value={detail.client?.email || '-'} />
            <Info label={t('profile.phone')} value={detail.client?.phone || '-'} />
          </div>
        )}
      </Modal>
      <QuickContactModal isOpen={quickContactOpen} onClose={() => setQuickContactOpen(false)} onCreated={handleQuickContactCreated} />
    </section>
  );
};

const Info = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 p-3">
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

export default InvolvedTab;
